import { useMemo } from 'react';
import { useConversations } from '@/hooks/use-conversations-v2';
import { conversationToDeal } from '@/types';
import {
  MarketRates,
  QuickCalc,
  PipelineSummary,
  PipelineMap,
} from '@/components/cockpit';

export function CockpitPage() {
  const {
    data: conversationsData,
    isLoading: convsLoading,
    error: convsError,
  } = useConversations();

  // PipelineMap is version-gated identically to PipelineSummary (ADR 0018):
  // a conversation only appears on the cockpit pipeline (list AND map) once it
  // has ≥1 version. The earlier "map shows all convs via pseudoPosition" intent
  // was reversed 2026-05-18 — a deal counted on the map but absent from the
  // version-gated list ("1 deal" vs "2 DEALS") is an inconsistency users see.
  // Same `hasVersion === true` predicate PipelineSummary applies internally.
  const deals = useMemo(() => {
    const convs = conversationsData?.data ?? [];
    return convs
      .filter((c) => !c.title.startsWith('[Solver]'))
      .filter((c) => c.hasVersion === true)
      .map(conversationToDeal);
  }, [conversationsData]);

  return (
    <div
      className="grid h-full overflow-hidden p-3 gap-2"
      style={{
        gridTemplateColumns: '1fr 1.1fr 1fr',
        gridTemplateRows: 'minmax(0, 1.05fr) minmax(0, 1fr)',
      }}
    >
      {/* Row 1, Col 1: Market Rates */}
      <MarketRates />

      {/* Row 1, Cols 2-3: Quick Calc (spans 2-3 per spec Decision §11)
       * `h-full min-h-0` makes the wrapper fill the grid-row height; the
       * inner Panel takes `h-full` to stretch within. Without these, the
       * Panel sizes to content and leaves dark space below the CTA on every
       * tab (cockpit-lovable-alignment /fix 2026-05-15). */}
      <div className="h-full min-h-0" style={{ gridColumn: '2 / 4' }}>
        <QuickCalc />
      </div>

      {/* Row 2, Cols 1-2: Pipeline — now self-managed (ADR 0018 Session 5) */}
      <PipelineSummary />

      {/* Row 2, Col 3: Pipeline Map (Phase 3 Session 3.3) */}
      <PipelineMap deals={deals} isLoading={convsLoading} error={convsError} />
    </div>
  );
}
